/**
 * Agents and automations: what runs without a person at the keyboard, how
 * often it runs, what fails and what it saves.
 *
 * Every figure comes from selectors.ts. The agents table asks for the `agents`
 * tier and the schedules table for the `inventory` tier, each on its own, so
 * the figures and the chart draw at once.
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Form, Table } from 'react-bootstrap';
import { Bar as BarChart } from 'react-chartjs-2';
import '../chartSetup';
import { fetchAgentDefinitionExport, type AgentExportFormat } from '@/services/agentDefinitionService';
import { ND_COLORS } from '@/styles/theme';
import type { ClientAccountRef } from '@/types/clientAccount';
import type { ClientSnapshot } from '@/types/fleetAnalytics';
import { FileExportService } from '@/utils/fileExport';
import { formatNzDate, formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import { useCurrency } from '../currencyContext';
import { RowDownloadMenu } from '../RowDownloadMenu';
import {
  isReady,
  needsOf,
  selectAgentRows,
  selectCounts,
  selectInventory,
  selectLifetimeTimeSaved,
  selectScheduled,
  selectSeries,
  selectTimeSaved,
} from '../selectors';
import type { AgentRows, AgentUsageRow, Inventory } from '../selectors';
import { fmtN, fmtUSD } from '../shared';
import { Empty, Kpi, KpiGrid, NeedsTier, Panel, Section } from '../ui/kit';
import { dayLabel, fmtMinutes, LinkButton, Quiet, SearchBox, sortRows, SortTh, tile, useSort } from './common';
import type { SortValue } from './common';
import type { DashboardTabProps } from './registry';
import './Agents.css';

// ─── words and formats ───────────────────────────────────────────────────

/** The one rule for time saved, as the nightly update works it out. Shown on hover of every time saved figure. */
const TIME_SAVED_RULE =
  'Each scheduled run of an agent counts as the minutes its owner estimated for that agent. An agent with no estimate adds nothing. Chat is not counted.';
/** What a time saved figure that follows the window can and cannot reach. */
const TIME_SAVED_REACH =
  'It is counted from dated run records. Each stack keeps these from a different day, so a window that starts before them reads low.';
/** Why the lifetime figure is its own number and never the sum of windows. */
const TIME_SAVED_TO_DATE =
  "It is counted from each schedule's own run counter, a different record from the window's figure, so the two do not add up.";

// ─── the tab ─────────────────────────────────────────────────────────────

export function Agents({ data, window, fleet, selection, onSelectClient }: DashboardTabProps) {
  useCurrency();
  const counts = useMemo(() => selectCounts(data), [data]);
  const scheduled = useMemo(() => selectScheduled(data, window), [data, window]);
  const timeSaved = useMemo(() => selectTimeSaved(data, window), [data, window]);
  const lifetimeSaved = useMemo(() => selectLifetimeTimeSaved(data), [data]);
  const isView = selection.kind === 'view';
  // On an older record the one time saved figure is the lifetime total already, tagged as such.
  const showLifetimeSaved = timeSaved.scope === 'window';

  // The rule, then how far back a windowed figure reaches. What the selector says is left out follows it on hover.
  const timeSavedHint = timeSaved.scope === 'window' ? `${TIME_SAVED_RULE} ${TIME_SAVED_REACH}` : TIME_SAVED_RULE;

  const anyFailed = scheduled.failed.value > 0;
  const failureRate = scheduled.failureRate.value;
  const failedSub =
    failureRate === null
      ? scheduled.failed.scope === 'window'
        ? 'No runs in this window'
        : 'No runs on record'
      : `${(100 * failureRate).toFixed(1)}% of ${scheduled.failed.scope === 'window' ? 'runs in this window' : 'all runs on record'}`;

  return (
    <>
      {/* What happened in the window, and its chart. */}
      <Section title="Scheduled runs">
        <KpiGrid cols={showLifetimeSaved ? 4 : 3}>
          <Kpi
            lead
            label="Scheduled runs"
            {...tile(
              scheduled.runs,
              fmtN,
              'Runs a schedule or an event started, counted from the record each run leaves.'
            )}
            sub={`${fmtUSD(scheduled.cost.value)} of model use`}
          />
          <Kpi
            label="Failed runs"
            {...tile(scheduled.failed, fmtN)}
            sub={failedSub}
            tone={anyFailed ? 'bad' : 'default'}
          />
          <Kpi
            label="Time saved"
            {...tile(timeSaved, fmtMinutes, timeSavedHint)}
            note={timeSaved.approx ? timeSaved.note : undefined}
            sub="As each agent's owner estimated it"
            tone={timeSaved.value > 0 ? 'good' : 'default'}
          />
          {showLifetimeSaved && (
            <Kpi
              label="Time saved to date"
              {...tile(lifetimeSaved, fmtMinutes, `${TIME_SAVED_RULE} ${TIME_SAVED_TO_DATE}`)}
              note={undefined}
              sub="Every run since each schedule was made"
            />
          )}
        </KpiGrid>
        <RunsChart data={data} window={window} />
      </Section>

      {/* What exists now, and the two lists of it. */}
      <Section title="Agents and schedules">
        <KpiGrid cols={4}>
          <Kpi
            label="Agents"
            {...tile(counts.agents, fmtN)}
            sub={`${fmtN(counts.workspaceAgents.value)} shared with a workspace, ${fmtN(counts.personalAgents.value)} personal`}
          />
          <Kpi
            label="Active schedules"
            {...tile(scheduled.activeSchedules, fmtN)}
            sub={`${fmtN(scheduled.schedules.value)} in all, deleted ones included`}
          />
          <Kpi
            label="Schedules with an error"
            {...tile(scheduled.schedulesWithErrors, fmtN)}
            sub="Their last run ended in an error"
            tone={scheduled.schedulesWithErrors.value > 0 ? 'warn' : 'default'}
          />
          <Kpi
            label="Projected runs a month"
            {...tile(scheduled.projectedRunsPerMonth, fmtN)}
            sub="From the schedules switched on now"
          />
        </KpiGrid>

        <AgentsPanel
          fleet={fleet}
          stacks={selection.stacks}
          window={window}
          isView={isView}
          onSelectClient={onSelectClient}
        />

        <SchedulesPanel fleet={fleet} stacks={selection.stacks} isView={isView} onSelectClient={onSelectClient} />
      </Section>
    </>
  );
}

// ─── scheduled runs per day ──────────────────────────────────────────────

/** A thin bar with a rounded top. `grouped: false` puts the two series on one bar, not side by side. */
const BAR = { borderRadius: 4, maxBarThickness: 24, grouped: false } as const;

function RunsChart({ data, window }: Pick<DashboardTabProps, 'data' | 'window'>) {
  const runs = useMemo(() => selectSeries(data, window, 'scheduledRuns'), [data, window]);
  const failed = useMemo(() => selectSeries(data, window, 'failedRuns'), [data, window]);

  if (!isReady(runs) || !runs.available) {
    return (
      <Panel title="Scheduled runs per day">
        <Empty>No scheduled run is on record.</Empty>
      </Panel>
    );
  }
  const marksFailures = isReady(failed) && failed.available;
  const subtitle = marksFailures
    ? `Failed runs are marked in red.${failed.note ? ` ${failed.note}` : ''}`
    : 'Failures by day are not recorded yet, so failed runs are not marked.';

  return (
    <Panel title="Scheduled runs per day" subtitle={subtitle}>
      <div className="dash-chart" role="img" aria-label="Bar chart of scheduled runs for each day of the window">
        <BarChart
          data={{
            labels: runs.days.map(dayLabel),
            datasets: [
              // The failed bar sits in front of the bar for all runs on the same day.
              ...(marksFailures
                ? [{ ...BAR, label: 'Failed', data: failed.values, backgroundColor: ND_COLORS.bad, order: 0 }]
                : []),
              { ...BAR, label: 'Runs', data: runs.values, backgroundColor: ND_COLORS.accent, order: 1 },
            ],
          }}
          options={{
            maintainAspectRatio: false,
            // One hover shows the day's runs and its failures together.
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { display: marksFailures, position: 'bottom', reverse: true, labels: { boxWidth: 12 } },
            },
            scales: {
              x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 14 } },
              y: { beginAtZero: true, ticks: { precision: 0 } },
            },
          }}
        />
      </div>
    </Panel>
  );
}

// ─── agents ──────────────────────────────────────────────────────────────

type AgentColumn =
  | 'title'
  | 'client'
  | 'adhoc'
  | 'scheduled'
  | 'adhocCost'
  | 'scheduledCost'
  | 'cost'
  | 'costPerScheduled'
  | 'timeSaved';

const AGENT_VALUE: Record<AgentColumn, (r: AgentUsageRow) => SortValue> = {
  title: (r) => r.title,
  client: (r) => r.client,
  adhoc: (r) => r.adhocConversations,
  scheduled: (r) => r.scheduledConversations,
  adhocCost: (r) => r.adhocCost,
  scheduledCost: (r) => r.scheduledCost,
  cost: (r) => r.cost,
  costPerScheduled: (r) => r.costPerScheduledConversation,
  timeSaved: (r) => r.timeSavedMinutes,
};
const AGENT_WORD_COLUMNS: ReadonlySet<AgentColumn> = new Set<AgentColumn>(['title', 'client']);

/** A fleet-wide view holds thousands of agents. This many rows are drawn until a person asks for the rest. */
const AGENT_ROW_LIMIT = 200;

/** Where to fetch an agent's definition from: the stack's own AWS account. */
function accountRefs(stacks: ClientSnapshot[]): Map<string, ClientAccountRef> {
  const out = new Map<string, ClientAccountRef>();
  for (const s of stacks) {
    const accountId = s.client_config?.client_account_id;
    const region = s.client_config?.region;
    if (accountId && region) out.set(s.client, { clientName: s.client, accountId, region });
  }
  return out;
}

interface AgentsPanelProps {
  fleet: DashboardTabProps['fleet'];
  stacks: ClientSnapshot[];
  window: DashboardTabProps['window'];
  isView: boolean;
  onSelectClient: DashboardTabProps['onSelectClient'];
}

function AgentsPanel({ fleet, stacks, window, isView, onSelectClient }: AgentsPanelProps) {
  const answer = useMemo(() => selectAgentRows(stacks, window), [stacks, window]);
  const [query, setQuery] = useState('');
  const [showIdle, setShowIdle] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const { sort, onSort } = useSort<AgentColumn>({ key: 'cost', dir: 'desc' }, AGENT_WORD_COLUMNS);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const refs = useMemo(() => accountRefs(stacks), [stacks]);

  // Time saved falls back to a lifetime figure that needs the agent lists.
  const { ensureTier } = fleet;
  useEffect(() => {
    needsOf(answer).forEach(ensureTier);
  }, [answer, ensureTier]);

  const ready: AgentRows | null = isReady(answer) ? answer : null;
  // "Active" can only mean "in the window" for a column that follows the window.
  const costWindowed = ready?.scopes.cost === 'window';
  const conversationsWindowed = ready?.scopes.conversations === 'window';
  const canHideIdle = costWindowed || conversationsWindowed;

  const visible = useMemo(() => {
    if (!ready) return [];
    const q = query.trim().toLowerCase();
    const rows = ready.rows.filter((r) => {
      if (q && !r.title.toLowerCase().includes(q) && !r.client.toLowerCase().includes(q)) return false;
      if (showIdle || !canHideIdle) return true;
      return (
        (costWindowed && r.cost > 0) ||
        (conversationsWindowed && (r.scheduledConversations > 0 || r.adhocConversations > 0))
      );
    });
    return sortRows(rows, AGENT_VALUE[sort.key], sort.dir);
  }, [ready, query, showIdle, canHideIdle, costWindowed, conversationsWindowed, sort]);

  const drawn = showAll ? visible : visible.slice(0, AGENT_ROW_LIMIT);
  const download = async (row: AgentUsageRow, format: AgentExportFormat) => {
    const ref = refs.get(row.client);
    if (!ref) return;
    FileExportService.downloadFile(await fetchAgentDefinitionExport(ref.clientName, row.agentId, format));
  };

  // A cost per conversation is only true when cost and conversations cover the same period.
  const showCostPerScheduled = ready ? ready.scopes.cost === ready.scopes.conversations : false;
  const scopes = ready?.scopes;
  const columns = 7 + (isView ? 1 : 0) + (showCostPerScheduled ? 1 : 0) + 1;

  return (
    <Panel
      title="Agents"
      subtitle={
        ready
          ? `${fmtN(visible.length)} of ${fmtN(ready.rows.length)} agents.${ready.note ? ` ${ready.note}` : ''}`
          : undefined
      }
      flush
      actions={
        <div className="dash-tools">
          {canHideIdle && (
            <Form.Check
              type="checkbox"
              id="agents-show-idle"
              label="Show agents with no activity in the window"
              checked={showIdle}
              onChange={(e) => setShowIdle(e.target.checked)}
            />
          )}
          <SearchBox value={query} onChange={setQuery} placeholder="Search agents" />
        </div>
      }
    >
      <NeedsTier fleet={fleet} tiers={ready ? ['agents'] : needsOf(answer)} what="Per-agent history">
        {downloadError && (
          <Alert variant="warning" dismissible onClose={() => setDownloadError(null)} className="m-2">
            The agent definition did not download: {downloadError}
          </Alert>
        )}
        <div className="dash-scroll">
          <Table className="dash-table">
            <thead>
              <tr>
                <SortTh column="title" sort={sort} onSort={onSort}>
                  Agent
                </SortTh>
                {isView && (
                  <SortTh column="client" sort={sort} onSort={onSort}>
                    Client
                  </SortTh>
                )}
                <SortTh
                  column="adhoc"
                  hint="Conversations a person started with the agent."
                  numeric
                  scope={scopes?.conversations}
                  sort={sort}
                  onSort={onSort}
                >
                  Conversations
                </SortTh>
                <SortTh
                  column="scheduled"
                  hint="Conversations a schedule or an event started with the agent. Scheduled runs are counted from run records, in the figure and the chart above."
                  numeric
                  scope={scopes?.conversations}
                  sort={sort}
                  onSort={onSort}
                >
                  Scheduled conversations
                </SortTh>
                <SortTh
                  column="adhocCost"
                  hint="Model use in conversations a person started."
                  numeric
                  scope={scopes?.cost}
                  sort={sort}
                  onSort={onSort}
                >
                  Spend from people
                </SortTh>
                <SortTh
                  column="scheduledCost"
                  hint="Model use in conversations a schedule or an event started."
                  numeric
                  scope={scopes?.cost}
                  sort={sort}
                  onSort={onSort}
                >
                  Spend from schedules
                </SortTh>
                <SortTh column="cost" numeric scope={scopes?.cost} sort={sort} onSort={onSort}>
                  Total spend
                </SortTh>
                {showCostPerScheduled && (
                  <SortTh
                    column="costPerScheduled"
                    hint="Spend from schedules over scheduled conversations."
                    numeric
                    scope={scopes?.cost}
                    sort={sort}
                    onSort={onSort}
                  >
                    Cost per scheduled conversation
                  </SortTh>
                )}
                <SortTh
                  column="timeSaved"
                  hint={TIME_SAVED_RULE}
                  numeric
                  scope={scopes?.timeSaved}
                  sort={sort}
                  onSort={onSort}
                >
                  Time saved
                </SortTh>
                <th className="dash-actions" aria-label="Download" />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={columns}>
                    <Empty>
                      {ready && ready.rows.length > 0
                        ? 'No agent matches. Clear the search, or show agents with no activity.'
                        : 'No agent has been used yet.'}
                    </Empty>
                  </td>
                </tr>
              )}
              {drawn.map((r) => (
                <tr key={r.key}>
                  <td className="dash-wrap">
                    <div className="dash-name">{r.title}</div>
                    {r.lastOptimisedAt && (
                      <div className="dash-sub" title={formatNzDateTime(r.lastOptimisedAt)}>
                        Optimised {timeAgo(r.lastOptimisedAt)}
                        {r.optimiseSessions > 1 ? `, ${fmtN(r.optimiseSessions)} sessions` : ''}
                      </div>
                    )}
                  </td>
                  {isView && (
                    <td>
                      <LinkButton onClick={() => onSelectClient(r.client)} title={`Open ${r.client}`}>
                        {r.client}
                      </LinkButton>
                    </td>
                  )}
                  <td className="text-end numeric">{fmtN(r.adhocConversations)}</td>
                  <td className="text-end numeric">{fmtN(r.scheduledConversations)}</td>
                  <td className="text-end numeric">{fmtUSD(r.adhocCost)}</td>
                  <td className="text-end numeric">{fmtUSD(r.scheduledCost)}</td>
                  <td className="text-end numeric">{fmtUSD(r.cost)}</td>
                  {showCostPerScheduled && (
                    <td className="text-end numeric">
                      {r.costPerScheduledConversation === null ? (
                        <Quiet title="No scheduled conversation in this period" />
                      ) : (
                        fmtUSD(r.costPerScheduledConversation)
                      )}
                    </td>
                  )}
                  <td className="text-end numeric">
                    {r.timeSavedMinutes === null ? (
                      <Quiet title="Loading the agent lists">…</Quiet>
                    ) : r.timeSavedMinutes === 0 ? (
                      <Quiet>{fmtMinutes(0)}</Quiet>
                    ) : (
                      fmtMinutes(r.timeSavedMinutes)
                    )}
                  </td>
                  <td className="dash-actions">
                    <RowDownloadMenu
                      title="Download the agent definition"
                      disabledReason={refs.has(r.client) ? undefined : 'This stack has no AWS account on record'}
                      onError={setDownloadError}
                      options={[
                        {
                          key: 'json',
                          label: 'Definition (.json)',
                          sublabel: 'The full agent record and its schedules',
                          run: () => download(r, 'json'),
                        },
                        {
                          key: 'md',
                          label: 'Readable (.md)',
                          sublabel: 'System prompt, tools, model and schedules',
                          run: () => download(r, 'md'),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
              {drawn.length < visible.length && (
                <tr>
                  <td colSpan={columns} className="agents-more">
                    The first {fmtN(drawn.length)} of {fmtN(visible.length)} agents are shown. Search to narrow the
                    list, or <LinkButton onClick={() => setShowAll(true)}>show all {fmtN(visible.length)}</LinkButton>.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </NeedsTier>
    </Panel>
  );
}

// ─── schedules ───────────────────────────────────────────────────────────

type ScheduleItem = Inventory['schedules'][number];
type ScheduleColumn = 'label' | 'client' | 'runs' | 'cadence' | 'status' | 'lastRun' | 'result' | 'total';

const SCHEDULE_STATUS: Record<
  string,
  { text: string; bg: 'success' | 'info' | 'warning' | 'secondary'; order: number }
> = {
  active: { text: 'Active', bg: 'success', order: 0 },
  scheduled: { text: 'Due to run', bg: 'info', order: 1 },
  pending_approval: { text: 'Waiting for approval', bg: 'warning', order: 2 },
  admin_locked: { text: 'Locked by an admin', bg: 'warning', order: 3 },
  paused: { text: 'Paused', bg: 'secondary', order: 4 },
  completed: { text: 'Finished', bg: 'secondary', order: 5 },
  deleted: { text: 'Deleted', bg: 'secondary', order: 6 },
};
const statusOf = (s: ScheduleItem) => s.derived_status || s.status;
const statusMeta = (s: ScheduleItem) =>
  SCHEDULE_STATUS[statusOf(s)] ?? { text: statusOf(s), bg: 'secondary' as const, order: 9 };

/** What the schedule starts: an agent by name, or an app. */
const runsWhat = (s: ScheduleItem) => (s.trigger_type === 'application' ? 'An app' : s.agent_title || 'Unnamed agent');

/** The stored timetable with its wrapper taken off: `0 8 ? * MON-FRI *`. */
const timetable = (s: ScheduleItem) => (s.cron_expression || '').replace(/^cron\(/, '').replace(/\)$/, '');

/** How often a schedule runs, in words. A schedule with no timetable is started by an event. */
function cadence(s: ScheduleItem): { text: string; detail?: string } {
  if (!s.cron_expression) return { text: 'On an event' };
  if (s.cron_kind === 'recurring')
    return { text: `About ${fmtN(s.projected_runs_per_month)} runs a month`, detail: timetable(s) };
  if (s.cron_kind === 'one-off')
    return { text: s.cron_state === 'expired' ? 'Once, date passed' : 'Once', detail: timetable(s) };
  return { text: 'Timetable', detail: timetable(s) };
}

/** When the schedule last ran, in milliseconds. Older records hold seconds. */
function lastRunMs(s: ScheduleItem): number | null {
  if (!s.last_run_epoch) return null;
  return s.last_run_epoch < 1e12 ? s.last_run_epoch * 1000 : s.last_run_epoch;
}

const RESULT_ORDER: Record<string, number> = { failed: 0, partial: 1, success: 2 };

const SCHEDULE_VALUE: Record<ScheduleColumn, (s: ScheduleItem) => SortValue> = {
  label: (s) => s.label,
  client: (s) => s.client,
  runs: (s) => runsWhat(s),
  cadence: (s) => (s.cron_kind === 'recurring' ? s.projected_runs_per_month : null),
  status: (s) => statusMeta(s).order,
  lastRun: (s) => lastRunMs(s),
  result: (s) => (s.last_status ? (RESULT_ORDER[s.last_status] ?? 3) : null),
  total: (s) => s.total_runs,
};
const SCHEDULE_WORD_COLUMNS: ReadonlySet<ScheduleColumn> = new Set<ScheduleColumn>([
  'label',
  'client',
  'runs',
  'status',
  'result',
]);

interface SchedulesPanelProps {
  fleet: DashboardTabProps['fleet'];
  stacks: ClientSnapshot[];
  isView: boolean;
  onSelectClient: DashboardTabProps['onSelectClient'];
}

function SchedulesPanel({ fleet, stacks, isView, onSelectClient }: SchedulesPanelProps) {
  const answer = useMemo(() => selectInventory(stacks), [stacks]);
  const [query, setQuery] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const { sort, onSort } = useSort<ScheduleColumn>({ key: 'status', dir: 'asc' }, SCHEDULE_WORD_COLUMNS);

  const all = isReady(answer) ? answer.schedules : null;
  const deleted = useMemo(() => (all || []).filter((s) => statusOf(s) === 'deleted').length, [all]);

  const visible = useMemo(() => {
    if (!all) return [];
    const q = query.trim().toLowerCase();
    const rows = all.filter((s) => {
      if (!showDeleted && statusOf(s) === 'deleted') return false;
      if (!q) return true;
      return [s.label, s.agent_title, s.client].some((v) => (v || '').toLowerCase().includes(q));
    });
    // Within one value of the sorted column, the busiest schedule comes first.
    return sortRows(sortRows(rows, SCHEDULE_VALUE.total, 'desc'), SCHEDULE_VALUE[sort.key], sort.dir);
  }, [all, query, showDeleted, sort]);

  const columns = isView ? 8 : 7;

  return (
    <Panel
      title="Schedules"
      subtitle={all ? `${fmtN(visible.length)} of ${fmtN(all.length)} schedules.` : undefined}
      flush
      actions={
        <div className="dash-tools">
          {deleted > 0 && (
            <Form.Check
              type="checkbox"
              id="agents-show-deleted"
              label={`Show ${fmtN(deleted)} deleted`}
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
          )}
          <SearchBox value={query} onChange={setQuery} placeholder="Search schedules" />
        </div>
      }
    >
      <NeedsTier fleet={fleet} tiers={all ? ['inventory'] : needsOf(answer)} what="The list of schedules">
        <div className="dash-scroll">
          <Table className="dash-table">
            <thead>
              <tr>
                <SortTh column="label" sort={sort} onSort={onSort}>
                  Schedule
                </SortTh>
                {isView && (
                  <SortTh column="client" sort={sort} onSort={onSort}>
                    Client
                  </SortTh>
                )}
                <SortTh column="runs" sort={sort} onSort={onSort}>
                  What it runs
                </SortTh>
                <SortTh column="cadence" sort={sort} onSort={onSort}>
                  How often
                </SortTh>
                <SortTh column="status" sort={sort} onSort={onSort}>
                  Status
                </SortTh>
                <SortTh column="lastRun" sort={sort} onSort={onSort}>
                  Last run
                </SortTh>
                <SortTh column="result" sort={sort} onSort={onSort}>
                  Last result
                </SortTh>
                <SortTh
                  column="total"
                  hint="Every run since the schedule was made."
                  numeric
                  scope="all-time"
                  sort={sort}
                  onSort={onSort}
                >
                  Runs
                </SortTh>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={columns}>
                    <Empty>{all && all.length > 0 ? 'No schedule matches.' : 'No schedules yet.'}</Empty>
                  </td>
                </tr>
              )}
              {visible.map((s) => {
                const status = statusMeta(s);
                const how = cadence(s);
                const ranAt = lastRunMs(s);
                return (
                  <tr key={`${s.client}::${s.schedule_id}`}>
                    <td className="dash-wrap">
                      {s.label ? <div className="dash-name">{s.label}</div> : <Quiet>No name</Quiet>}
                    </td>
                    {isView && (
                      <td>
                        <LinkButton onClick={() => onSelectClient(s.client)} title={`Open ${s.client}`}>
                          {s.client}
                        </LinkButton>
                      </td>
                    )}
                    <td className="dash-wrap">{runsWhat(s)}</td>
                    <td>
                      {how.text}
                      {how.detail && <div className="dash-sub mono">{how.detail}</div>}
                    </td>
                    <td>
                      <Badge bg={status.bg}>{status.text}</Badge>
                    </td>
                    <td>
                      {ranAt === null ? (
                        <Quiet>Never</Quiet>
                      ) : (
                        <div title={formatNzDateTime(ranAt)}>
                          {timeAgo(ranAt)}
                          <div className="dash-sub">{formatNzDate(ranAt)}</div>
                        </div>
                      )}
                    </td>
                    <td>
                      {s.last_status === 'failed' ? (
                        <Badge bg="danger">Failed</Badge>
                      ) : s.last_status === 'partial' ? (
                        <Badge bg="warning">Partly failed</Badge>
                      ) : s.last_status === 'success' ? (
                        'Succeeded'
                      ) : (
                        <Quiet>{s.last_status || 'None'}</Quiet>
                      )}
                    </td>
                    <td className="text-end numeric">{fmtN(s.total_runs)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </NeedsTier>
    </Panel>
  );
}
